import axios from 'axios';

/**
 * Multi-Protocol API Configuration
 * Supports both HTTPS (https://coreapi.corespectators.com/api) and HTTP (http://coreapi.corespectators.com/api)
 */
const HTTPS_URL = 'https://coreapi.corespectators.com/api';
const HTTP_URL = 'http://coreapi.corespectators.com/api';

export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    let custom = process.env.REACT_APP_API_URL.trim().replace(/\/+$/, '');
    return custom.endsWith('/api') ? custom : `${custom}/api`;
  }

  // Dynamic protocol matching in browser to prevent mixed content
  if (typeof window !== 'undefined' && window.location) {
    if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
      return HTTP_URL;
    }
  }

  return HTTPS_URL;
};

export const API_BASE_URL = getApiBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

/**
 * Resilient request executor that attempts HTTPS first and falls back to HTTP
 * if a network/SSL error occurs.
 */
const executeWithFallback = async (method, endpoint, data = null) => {
  const primaryUrl = getApiBaseUrl();
  const alternateUrl = primaryUrl.startsWith('https://') 
    ? HTTP_URL 
    : HTTPS_URL;

  try {
    const response = await apiClient({
      method,
      url: endpoint,
      data,
    });
    return response.data;
  } catch (primaryError) {
    // If network error, attempt alternate protocol
    if (!primaryError.response && alternateUrl !== primaryUrl) {
      console.warn(`[API] Primary request to ${primaryUrl} failed. Retrying via ${alternateUrl}...`);
      try {
        const fallbackClient = axios.create({
          baseURL: alternateUrl,
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
        });
        const fallbackResponse = await fallbackClient({
          method,
          url: endpoint,
          data,
        });
        return fallbackResponse.data;
      } catch (fallbackError) {
        throw fallbackError.response ? fallbackError : primaryError;
      }
    }
    throw primaryError;
  }
};

/**
 * Submit Contact Us Message
 * @param {Object} data { name, email, phone, message }
 */
export const submitContactForm = async (data) => {
  return await executeWithFallback('post', '/contact', data);
};

/**
 * Submit Requirement / Project Quote Form
 * @param {Object} data formData
 */
export const submitRequirementForm = async (data) => {
  return await executeWithFallback('post', '/submit_form', data);
};

export default apiClient;
